import { useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

//Primereact components
import { Toast } from 'primereact/toast';
import { Toolbar } from 'primereact/toolbar';
import { Button } from 'primereact/button';
import { DataTable } from 'primereact/datatable';
import { Column } from 'primereact/column';
import { InputText } from 'primereact/inputtext';
import { Calendar } from 'primereact/calendar';

// Column
import { ElectoralColumns } from '../../../appconfig/DatatableSetting';

// Data table
import { defaultRowOptions, defaultPageRowOptions, Skeletonitems, SkeletonbodyTemplate, paginatorLinks, showingEntries, ELECTORAL } from '../../../appconfig/Settings';

import { DOWNLOADELECTORALAPPLICATIONREPORT, OTHER_APPLIST } from '../../../config/ApiConstant';
import { ApplicationTab } from '../applicationTab';
import { ElectionApplicationTab } from './ElectionApplicationTab';

//Services
import { getAllDetails } from '../../../service/ProductService';
import moment from 'moment';
import { downloadData, formatDateTime, getAction } from '../../../appconfig/AppHelper';
import { createdAtTemplate } from '../../../appconfig/DatatableTemplateSetting';
import CustomDatePicker from '../../../components/CustomDatePicker/CustomDatePicker';

export const Electoral = () => {
    document.title = 'Electoral Roll Fees Application | GSPC';

    //Set Toast/ Filter Properties
    const toast = useRef<any>(null);
    const [globalFieldFilter, setGlobalFieldFilter] = useState<any>(null);
    const [globalFilter, setGlobalFilter] = useState<any>(null);

    // Date Object
    const storedDates = localStorage.getItem('electoral_app');
    let initialStartDate = moment().subtract(30, 'days').toDate();
    let initialEndDate = moment().toDate();

    if (storedDates) {
        try {
            const parsed = JSON.parse(storedDates);
            initialStartDate = parsed.startDate ? new Date(parsed.startDate) : initialStartDate;
            initialEndDate = parsed.endDate ? new Date(parsed.endDate) : initialEndDate;
        } catch (error) {
            console.error('Error parsing stored dates', error);
        }
    }

    const [dates, setDates] = useState({
        start: initialStartDate,
        end: initialEndDate
    });

    //Loading/Page Loading
    const [pageLoad, setPageLoad] = useState<boolean>(false);
    const [applicationsList, setApplicationsList] = useState<any>([]);
    const [downloadLoader, setDownloadLoader] = useState<boolean>(false);

    // Navigate
    const navigate = useNavigate();

    // useEffect
    useEffect(() => {
        getElectoralApplication();

        window.scrollTo({ top: 0, behavior: 'smooth' });
    }, [dates]);

    // Get electoral applications
    const getElectoralApplication = async () => {
        try {
            setPageLoad(false);
            let startDate = moment(dates?.start).format('y-M-D') + ' 00:00:00';
            let endDate = moment(dates?.end).format('y-M-D') + ' 23:59:59';
            const response = await getAllDetails(OTHER_APPLIST + '?AppNm=' + ELECTORAL + '&start_date=' + startDate + '&end_date=' + endDate);
            if (response) {
                const DataList: any = response;
                if (DataList.length === 0) {
                    setApplicationsList([]);
                } else {
                    setApplicationsList(DataList);
                }
                setPageLoad(true);
            } else {
                setPageLoad(false);
                setApplicationsList([]);
            }
        } catch (error: any) {
            setPageLoad(false);
            setApplicationsList([]);
            toast.current?.show({
                severity: 'error',
                summary: 'Error',
                detail: 'Sorry Could Not Load Data'
            });
        }
    };

    // On Date Change
    const onDateChange = (start: any, end: any) => {
        setDates({ start, end });

        if (start && end) {
            localStorage.setItem(
                'electoral_app',
                JSON.stringify({
                    startDate: moment(start).format('YYYY-MM-DD'),
                    endDate: moment(end).format('YYYY-MM-DD')
                })
            );
        }
    };

    // Global search
    const leftToolbarTemplate = () => {
        return (
            <>
                <span className="search-bar mr-3">
                    <i className="pi pi-search"></i>
                    <InputText type="search" className="search-input" onInput={(e) => setGlobalFilter(e.currentTarget.value)} placeholder="Search..." />
                </span>
            </>
        );
    };

    // Right toolbar
    const rightToolbarTemplate = () => {
        return (
            <>
                <CustomDatePicker startDate={dates.start} endDate={dates.end} onChange={onDateChange} />

                <Button icon="pi pi-download" label="Download" onClick={() => download()} className="ml-2" loading={downloadLoader} disabled={!window.cn(applicationsList) && applicationsList.length > 0 ? false : true} />
                <Button icon="pi pi-filter-slash" label="Clear" onClick={() => clearAll()} className="p-button-outlined ml-2"></Button>
            </>
        );
    };

    //Clear All Filter
    const clearAll = () => {
        setGlobalFieldFilter(null);
        setGlobalFilter(null);
    };

    // Download file
    const download = async () => {
        try {
            setDownloadLoader(true);
            let formData: any = new FormData();

            let startDate = moment(dates?.start).format('y-M-D') + ' 00:00:00';
            let endDate = moment(dates?.end).format('y-M-D') + ' 23:59:59';

            formData.append('start_date', startDate);
            formData.append('end_date', endDate);
            formData.append('application_name', ELECTORAL);

            // get pdf data
            let res = await downloadData(DOWNLOADELECTORALAPPLICATIONREPORT, 'Electoral_Roll_Fees_Application_Report', formData);
            if (res === true) {
                setDownloadLoader(false);
            } else {
                setDownloadLoader(false);
                toast.current?.show({
                    severity: 'error',
                    summary: 'Error',
                    detail: 'Sorry Could Not Download File'
                });
            }
        } catch (error: any) {
            setDownloadLoader(false);
            toast.current?.show({
                severity: 'error',
                summary: 'Error',
                detail: 'Sorry Could Not Download File'
            });
        }
    };

    // Template for validity date
    const LastVDateTemplate = (rowData: any, rowIndex: any) => {
        return (
            <>
                <div>{!window.cn(rowData?.applicant_details.LastVDate) ? formatDateTime(rowData?.applicant_details.LastVDate) : '-'}</div>
            </>
        );
    };

    return (
        <>
            <ApplicationTab mainTabLabel="Election Application"  />
            <ElectionApplicationTab initialTabLabel="Electoral Roll Fees Application" />
            <Toast ref={toast} />
            <div className="grid crud-demo">
                <div className="col-12">
                    <div className="card">
                        <Toolbar left={leftToolbarTemplate} right={rightToolbarTemplate}></Toolbar>

                        {/* Datatable */}
                        {pageLoad ? (
                            <>
                                <DataTable
                                    value={applicationsList}
                                    showGridlines
                                    stripedRows
                                    globalFilter={globalFilter}
                                    filters={globalFieldFilter}
                                    rows={defaultRowOptions}
                                    paginator={applicationsList.length >= 10 && true}
                                    rowsPerPageOptions={defaultPageRowOptions}
                                    onFilter={(e) => setGlobalFieldFilter(e.filters)}
                                    className="datatable-responsive p-datatable-sm"
                                    emptyMessage="No Applications Found"
                                    paginatorTemplate={paginatorLinks}
                                    currentPageReportTemplate={showingEntries}
                                >
                                    {ElectoralColumns.map((col, i) => {
                                        if (col.field === 'LastVDate') {
                                            return <Column key={col.field} field={col.field} header={col.header} body={LastVDateTemplate} sortable />;
                                        } else if (col.field === 'created_at') {
                                            return <Column key={col.field} field={col.field} header={col.header} body={createdAtTemplate} sortable />;
                                        } else {
                                            return <Column key={col.field} field={col.field} header={col.header} filter filterPlaceholder={'Search by ' + col.header} />;
                                        }
                                    })}
                                </DataTable>
                                {/* <Paginator first={first} rows={perPage} totalRecords={totalRecords} rowsPerPageOptions={defaultPageRowOptions} onPageChange={onPageChange} /> */}
                            </>
                        ) : (
                            <>
                                {/* Skeleton Data table */}
                                <DataTable value={Skeletonitems}>
                                    {ElectoralColumns.map((col, i) => (
                                        <Column key={col.field} field={col.field} header={col.header} body={SkeletonbodyTemplate} />
                                    ))}
                                </DataTable>
                            </>
                        )}
                    </div>
                </div>
            </div>
        </>
    );
};
